import type { BallState } from '../sim/state';
import type { BallConfig } from '../config/simConfig';
import type { ConditionParams } from '../config/conditions';

/**
 * Integrate the ball one tick (§5.3). Semi-implicit Euler with the pinned sub-step order:
 * all accelerations are evaluated from start-of-tick values, then velocity is integrated, then
 * spin decays, then position is integrated, then the ground is resolved (bounce or rest snap).
 * Mutates `ball` in place (called on step's working clone). Pure otherwise.
 *
 * Grounded contact: a ball resting/rolling on the pitch (z ≤ 0 and vz ≤ 0) has gravity cancelled by
 * the normal force and rolling resistance applied to v_xy; it never spuriously falls or bounces. A
 * ball only bounces when it crosses z = 0 while descending (was airborne at tick start).
 */
export function stepBall(ball: BallState, cond: ConditionParams, cfg: BallConfig, dt: number): void {
  const { pos, vel, spin } = ball;
  const grounded = pos.z <= 0 && vel.z <= 0;

  // --- accelerations from start-of-tick state ---
  const speed = Math.hypot(vel.x, vel.y, vel.z);
  let ax = 0;
  let ay = 0;
  let az = 0;

  if (!grounded) az -= cfg.GRAVITY; // normal force cancels gravity while resting on the pitch

  // air drag: a = −KD_AIR·|v|·v (all components; §5.3)
  const kd = cfg.KD_AIR * speed;
  ax -= kd * vel.x;
  ay -= kd * vel.y;
  az -= kd * vel.z;

  // rolling resistance (viscous, 1/s): a_roll = −KF_ROLL·v_xy — grounded only (§5.3)
  if (grounded) {
    ax -= cond.KF_ROLL * vel.x;
    ay -= cond.KF_ROLL * vel.y;
  }

  // Magnus: a = KM·(ω × v), ω from start-of-tick (pre-decay; §5.3)
  const cx = spin.y * vel.z - spin.z * vel.y;
  const cy = spin.z * vel.x - spin.x * vel.z;
  const cz = spin.x * vel.y - spin.y * vel.x;
  ax += cfg.KM * cx;
  ay += cfg.KM * cy;
  az += cfg.KM * cz;

  // --- integrate velocity ---
  vel.x += ax * dt;
  vel.y += ay * dt;
  vel.z += az * dt;
  if (grounded && vel.z < 0) vel.z = 0; // ground holds a resting ball (no downward accumulation)

  // --- spin decays after acceleration eval, then clamp |ω| ≤ MAX_SPIN (§5.3) ---
  const decay = 1 - cfg.SPIN_DECAY * dt;
  spin.x *= decay;
  spin.y *= decay;
  spin.z *= decay;
  const wmag = Math.hypot(spin.x, spin.y, spin.z);
  if (wmag > cfg.MAX_SPIN) {
    const s = cfg.MAX_SPIN / wmag;
    spin.x *= s;
    spin.y *= s;
    spin.z *= s;
  }

  // --- integrate position ---
  pos.x += vel.x * dt;
  pos.y += vel.y * dt;
  pos.z += vel.z * dt;

  // --- ground collision / bounce ---
  if (pos.z < 0) {
    pos.z = 0;
    if (vel.z < 0) {
      vel.z = -cond.REST * vel.z; // reflect (§5.3 REST[surface])
      vel.x *= 1 - cfg.BOUNCE_GRIP; // horizontal grip loss
      vel.y *= 1 - cfg.BOUNCE_GRIP;
      // NOTE (§5.3): spin↔velocity exchange on bounce (v_xy += ω_topspin·R·GRIP_K) is deferred
      // until a test pins the ω_topspin interpretation — tracked in PROGRESS.md (M1/S3).
    }
  }

  // --- rest snap: kill the tail of a slow grounded ball (§5.3) ---
  if (pos.z <= 0 && Math.hypot(vel.x, vel.y, vel.z) < cfg.REST_SNAP_SPEED) {
    vel.x = 0;
    vel.y = 0;
    vel.z = 0;
  }
}
